import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

const EMPTY = {
  borrower_id: "", cif: "", name: "", borrower_type: "Corporate",
  sector: "Manufaktur", country: "Indonesia", risk_rating: "BBB",
  status: "Active", business_unit: "Korporasi Jakarta",
  group_id: "", shareholder_ids: [], address: "", director: "",
};

export default function BorrowerFormDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [groups, setGroups] = useState([]);
  const [shareholders, setShareholders] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/group-obligors").then((r) => setGroups(r.data));
      api.get("/shareholders").then((r) => setShareholders(r.data));
      setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
    }
  }, [open, initial]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const handleSubmit = async () => {
    if (!form.borrower_id || !form.name) {
      toast.error("Borrower ID dan Nama wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (initial) {
        await api.put(`/borrowers/${initial.borrower_id}`, payload);
        toast.success("Borrower diupdate");
      } else {
        await api.post("/borrowers", payload);
        toast.success("Borrower dibuat");
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="borrower-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit Borrower" : "Tambah Borrower"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Borrower ID" required>
            <Input value={form.borrower_id} disabled={!!initial} onChange={(e) => set("borrower_id", e.target.value)} className="mono" data-testid="borrower-id-input" />
          </Field>
          <Field label="CIF">
            <Input value={form.cif} onChange={(e) => set("cif", e.target.value)} className="mono" data-testid="borrower-cif-input" />
          </Field>
          <Field label="Nama" required className="col-span-2">
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="borrower-name-input" />
          </Field>
          <Field label="Tipe Borrower">
            <Select value={form.borrower_type} onValueChange={(v) => set("borrower_type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Corporate">Corporate</SelectItem>
                <SelectItem value="SME">SME</SelectItem>
                <SelectItem value="Commercial">Commercial</SelectItem>
                <SelectItem value="Individual">Individual</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Sektor">
            <Input value={form.sector} onChange={(e) => set("sector", e.target.value)} />
          </Field>
          <Field label="Risk Rating">
            <Select value={form.risk_rating} onValueChange={(v) => set("risk_rating", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["AAA", "AA", "A", "BBB", "BB", "B", "CCC", "D"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Business Unit">
            <Input value={form.business_unit} onChange={(e) => set("business_unit", e.target.value)} />
          </Field>
          <Field label="Group Obligor">
            <Select value={form.group_id || "_none"} onValueChange={(v) => set("group_id", v === "_none" ? "" : v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">- Tidak ada -</SelectItem>
                {groups.map((g) => <SelectItem key={g.group_id} value={g.group_id}>{g.group_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Direktur">
            <Input value={form.director || ""} onChange={(e) => set("director", e.target.value)} />
          </Field>
          <Field label="Alamat" className="col-span-2">
            <Textarea value={form.address || ""} onChange={(e) => set("address", e.target.value)} rows={2} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="borrower-submit-button">
            {saving ? "Menyimpan…" : "Simpan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children, className }) {
  return (
    <div className={className}>
      <Label className="text-xs">{label}{required && <span className="text-[#D0021B]"> *</span>}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
